<script setup lang="ts">
import { useSlots, watch, onBeforeUnmount, nextTick, ref } from "vue";

const props = withDefaults(defineProps<{
    id: string;
    show: boolean;
    header?: string | undefined;
    openFocus?: string | undefined;
    closeFocus?: string | undefined;
    subheader?: string | undefined;
    styles?: string;
    cross?: boolean;
    overlay?: boolean;
    wide?: boolean;
}>(), {
    header: undefined,
    subheader: undefined,
    openFocus: "",
    closeFocus: "",
    cross: true,
    overlay: true,
    show: false,
    styles: "",
});

const dialogRef = ref<HTMLDialogElement | null>(null);

async function focusLoad() {
    document.body.classList.add("overflow-hidden");
    await nextTick();
    if (dialogRef.value) {
        dialogRef.value.showModal();
        dialogRef.value.focus();
    }
};

async function focusClose() {
    document.body.classList.remove("overflow-hidden");
    await nextTick();
    if (props.closeFocus) {
        document.getElementById(props.closeFocus)?.focus();
    }
};

watch(() => props.show, (showValue) => {
    if (showValue) {
        focusLoad();
    } else {
        focusClose();
    }
});

onBeforeUnmount(() => {
    if (props.show) {
        document.body.classList.remove("overflow-hidden");
        if (dialogRef.value) {
            dialogRef.value.close();
        }
        focusClose();
    }
});

const slots = useSlots();

const emit = defineEmits<{
    (on:"close"): void
}>();

</script>
<template>
    <div
        v-if="show"
        :id="props.id"
        class="overlay"
        @click.prevent="emit('close')"
    />
    <dialog
        v-if="show"
        ref="dialogRef"
        :class="show ? 'modal-show' : ''"
        class="modal"
    >
        <div
            class="modal-dialog"
            :class="props.wide ? 'modal-dialog-wide' : ''"
        >
            <div class="modal-content">
                <div v-if="slots.title">
                    <slot name="title" />
                </div>
                <div
                    v-if="header"
                    class="modal-header"
                >
                    <div>
                        <h5 class="modal-title">
                            {{ header }}
                        </h5>
                        <h6
                            v-if="subheader"
                            class="modal-sub-header"
                        >
                            {{ subheader }}
                        </h6>
                    </div>
                </div>
                <button
                    v-if="cross"
                    :id="`close-${props.id}`"
                    type="button"
                    class="btn-close"
                    aria-label="close"
                    @click.prevent="emit('close')"
                />
                <div
                    v-if="slots.body"
                    class="modal-body"
                >
                    <slot name="body" />
                </div>
                <div
                    v-if="slots.footer"
                    class="modal-footer"
                >
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </dialog>
</template>

<style>
    .z-index-1{
        z-index: 1;
    }
    .modal-background{
        width: 100%;
    }
    .modal-show {
        position: fixed;
        display: block;
        overflow-x: hidden;
        overflow-y: auto;
        outline: 0;
        z-index: 9;
    }
    .modal-header {
        position: relative;
    }
    .modal-sub-header {
        margin-top: 10px;
    }
    .modal-header .btn-close {
        position: absolute;
        top: 10px;
        right: 10px;
    }
    dialog {
        background: transparent;
        border: none;
        max-width: 100%;
        max-height: 100%;
    }
    @media print {
        .modal-show {
            position: relative;
            overflow: visible;
        }
    }
</style>
